import { useEffect } from 'react';

import { readableTextColor } from '../../../../../utils/readableTextColor';

import {
	CheckoutFont,
	CheckoutRadius,
	ICheckoutPageSettings
} from '../../../../../api/checkouts/types/checkout-page.type';

/** Raio dos cards; campos, abas e botões usam ~2/3 disso (ver o SCSS). */
export const RADIUS_PX: Record<CheckoutRadius, number> = {
	none: 0,
	sm: 6,
	md: 12,
	lg: 18,
	xl: 28
};

/** Família CSS e a URL do Google Fonts (só a escolhida é carregada). */
export const FONTS: Record<
	Exclude<CheckoutFont, 'default'>,
	{ family: string; query: string }
> = {
	inter: { family: "'Inter'", query: 'Inter:wght@400;500;600;700' },
	poppins: { family: "'Poppins'", query: 'Poppins:wght@400;500;600;700' },
	montserrat: {
		family: "'Montserrat'",
		query: 'Montserrat:wght@400;500;600;700'
	},
	roboto: { family: "'Roboto'", query: 'Roboto:wght@400;500;700' },
	'open-sans': {
		family: "'Open Sans'",
		query: 'Open+Sans:wght@400;500;600;700'
	},
	lato: { family: "'Lato'", query: 'Lato:wght@400;700' },
	nunito: { family: "'Nunito'", query: 'Nunito:wght@400;600;700' },
	'dm-sans': { family: "'DM Sans'", query: 'DM+Sans:wght@400;500;700' },
	playfair: {
		family: "'Playfair Display'",
		query: 'Playfair+Display:wght@400;600;700'
	}
};

/** Cor → variável CSS (lidas pelo checkout-modern.scss e pelos blocos). */
const COLOR_VARS = {
	pageBg: '--checkout-page-bg',
	cardBg: '--checkout-card-bg',
	text: '--checkout-text',
	mutedText: '--checkout-muted',
	border: '--checkout-border'
} as const;

/**
 * O que a aparência faz no documento, em dado puro (testável): classes no
 * `<html>` e variáveis CSS. As cores só entram com `theme: 'custom'`.
 */
export const appearanceToCss = (settings?: ICheckoutPageSettings) => {
	const appearance = settings?.appearance;
	const classes: string[] = [];
	const vars: Record<string, string> = {};

	if (settings?.theme === 'custom' && appearance) {
		classes.push('checkout-custom-theme');
		for (const [key, cssVar] of Object.entries(COLOR_VARS)) {
			const value = appearance[key as keyof typeof COLOR_VARS];
			if (value) vars[cssVar] = value;
		}
		// Cor principal também é o destaque (abas, realces) quando o admin
		// não escolheu uma cor própria para o botão.
		if (appearance.text && !settings.accentColor) {
			vars['--checkout-accent'] = appearance.text;
			vars['--checkout-accent-soft'] = `${appearance.text}1A`;
		}
	}
	if (appearance?.radius) {
		classes.push('checkout-has-radius');
		vars['--checkout-radius'] = `${RADIUS_PX[appearance.radius]}px`;
	}
	if (appearance?.font && appearance.font !== 'default') {
		const font = FONTS[appearance.font];
		if (font) {
			classes.push('checkout-has-font');
			vars['--checkout-font'] = `${font.family}, Helvetica, sans-serif`;
		}
	}
	return { classes, vars };
};

/**
 * COR PRINCIPAL do tema personalizado (o campo `appearance.text` — no builder,
 * "Cor principal"): títulos, botões de compra, plano selecionado e o selo
 * "Recomendado". Fora do tema personalizado não existe.
 */
export const principalColor = (
	settings?: ICheckoutPageSettings
): string | null =>
	settings?.theme === 'custom' ? settings.appearance?.text || null : null;

/**
 * Cores do botão de ação (comprar, aceitar oferta, acessar a plataforma).
 *
 * 1. cor configurada para o botão (`override`, ou `accentColor` da página) —
 *    escolha explícita do admin, texto como sempre foi;
 * 2. cor principal do tema personalizado — texto AUTOMÁTICO (grafite ou
 *    branco, o que contrastar mais): a cor de apoio costuma ser um tom médio e
 *    ficaria ilegível sobre a principal;
 * 3. cor da plataforma.
 */
export const checkoutButtonColors = ({
	settings,
	override,
	platformPrimary,
	platformSecondary
}: {
	settings?: ICheckoutPageSettings;
	override?: string | null;
	platformPrimary?: string | null;
	platformSecondary?: string | null;
}): { backgroundColor?: string; color?: string } => {
	const explicit = override || settings?.accentColor;
	if (explicit) {
		return {
			backgroundColor: explicit,
			color: platformSecondary || '#FFF'
		};
	}
	const principal = principalColor(settings);
	if (principal) {
		return {
			backgroundColor: principal,
			color: readableTextColor(principal)
		};
	}
	return {
		backgroundColor: platformPrimary || undefined,
		color: platformSecondary || '#FFF'
	};
};

const FONT_LINK_ID = 'checkout-appearance-font';

/** Aplica a aparência no `<html>` e desfaz ao trocar/sair (preview do builder). */
export const useCheckoutAppearance = (settings?: ICheckoutPageSettings) => {
	const key = JSON.stringify({
		theme: settings?.theme,
		appearance: settings?.appearance
	});

	useEffect(() => {
		const root = document.documentElement;
		const { classes, vars } = appearanceToCss(settings);

		classes.forEach((name) => root.classList.add(name));
		Object.entries(vars).forEach(([name, value]) =>
			root.style.setProperty(name, value)
		);

		const font = settings?.appearance?.font;
		if (font && font !== 'default' && FONTS[font]) {
			let link = document.getElementById(
				FONT_LINK_ID
			) as HTMLLinkElement | null;
			if (!link) {
				link = document.createElement('link');
				link.id = FONT_LINK_ID;
				link.rel = 'stylesheet';
				document.head.appendChild(link);
			}
			link.href = `https://fonts.googleapis.com/css2?family=${FONTS[font].query}&display=swap`;
		}

		return () => {
			classes.forEach((name) => root.classList.remove(name));
			Object.keys(vars).forEach((name) =>
				root.style.removeProperty(name)
			);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [key]);
};
